(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-fajmp]';
  const GLOBAL_KEY = '__fajModernProduct';

  /* This asset may be emitted once per section. Reuse the first controller
     so the same product form never receives duplicate submit listeners. */
  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initAll(document);
    return;
  }

  const instances = new WeakMap();

  class FAJModernProduct {
    constructor(root) {
      this.root = root;
      this.mainGallery = root.querySelector('[data-fajmp-main-gallery]');
      this.galleryWrap = root.querySelector('[data-fajmp-gallery-wrap]');
      this.cells = Array.from(root.querySelectorAll('[data-fajmp-media-cell]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-fajmp-thumb]'));
      this.form = root.querySelector('[data-fajmp-product-form]');
      this.zoomPanel = root.querySelector('[data-fajmp-zoom-panel]');
      this.zoomImage = root.querySelector('[data-fajmp-zoom-image]');
      this.tabs = Array.from(root.querySelectorAll('[data-fajmp-tab]'));
      this.tabPanels = Array.from(root.querySelectorAll('[data-fajmp-tab-panel]'));
      this.layout = root.querySelector('[data-fajmp-layout]');
      this.stickyAncestors = [];
      this.productData = this.readProductData();
      this.currentVariant = this.productData.variants.find(
        (variant) => String(variant.id) === String(this.productData.selectedVariantId)
      ) || this.productData.variants[0];
      this.activeMediaIndex = Number.parseInt(root.dataset.initialMediaIndex, 10) || 0;
      this.flickity = null;
      this.isAdding = false;
      this.addController = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.hideZoom = this.hideZoom.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);

      this.mountZoomPanel();
      this.bind();
      this.initGallery();
      this.initTabs();
      this.updateVariant(this.currentVariant, { updateUrl: false, selectMedia: false });
      this.prepareNativeSticky();
    }

    readProductData() {
      const script = this.root.querySelector('[data-fajmp-product-json]');
      if (!script) return { variants: [] };
      try { return JSON.parse(script.textContent); }
      catch (error) {
        console.error('[FAJ product] Invalid product JSON', error);
        return { variants: [] };
      }
    }

    bind() {
      this.root.addEventListener('click', this.handleClick);
      this.root.addEventListener('change', this.handleChange);
      this.form?.addEventListener('submit', this.handleSubmit, true);
      this.root.addEventListener('keydown', this.handleKeydown);
      this.galleryWrap?.addEventListener('pointermove', this.handlePointerMove);
      this.galleryWrap?.addEventListener('pointerleave', this.hideZoom);
      this.galleryWrap?.addEventListener('pointercancel', this.hideZoom);
    }

    destroy() {
      this.addController?.abort();
      this.root.removeEventListener('click', this.handleClick);
      this.root.removeEventListener('change', this.handleChange);
      this.form?.removeEventListener('submit', this.handleSubmit, true);
      this.root.removeEventListener('keydown', this.handleKeydown);
      this.galleryWrap?.removeEventListener('pointermove', this.handlePointerMove);
      this.galleryWrap?.removeEventListener('pointerleave', this.hideZoom);
      this.galleryWrap?.removeEventListener('pointercancel', this.hideZoom);
      this.restoreStickyAncestors();
      this.zoomPanel?.remove();
      this.flickity?.destroy();
    }

    prepareNativeSticky() {
      if (
        this.root.dataset.stickyColumns !== 'true' ||
        !window.matchMedia('(min-width: 1200px)').matches ||
        !this.layout
      ) {
        return;
      }

      /*
       * position: sticky is disabled when an ancestor clips overflow.
       * Only neutralize ancestors that are not real scrolling containers,
       * avoiding changes to intentional theme drawers or scroll areas.
       */
      let ancestor = this.root.parentElement;

      while (ancestor && ancestor !== document.body) {
        const styles = window.getComputedStyle(ancestor);
        const overflowValues = [
          styles.overflow,
          styles.overflowX,
          styles.overflowY
        ];

        const clipsSticky = overflowValues.some((value) =>
          ['hidden', 'clip', 'auto', 'scroll'].includes(value)
        );

        const isActualVerticalScroller =
          ancestor.scrollHeight > ancestor.clientHeight + 2 &&
          ['auto', 'scroll'].includes(styles.overflowY);

        if (clipsSticky && !isActualVerticalScroller) {
          ancestor.classList.add('fajmp-sticky-overflow-fix');
          this.stickyAncestors.push(ancestor);
        }

        ancestor = ancestor.parentElement;
      }
    }

    restoreStickyAncestors() {
      this.stickyAncestors.forEach((ancestor) => {
        ancestor.classList.remove('fajmp-sticky-overflow-fix');
      });

      this.stickyAncestors = [];
    }

    mountZoomPanel() {
      if (!this.zoomPanel) return;

      this.zoomPanel.hidden = true;
      this.zoomPanel.setAttribute('aria-hidden', 'true');
      this.zoomPanel.dataset.fajmpZoomPortal = this.root.dataset.sectionId || '';
      document.body.appendChild(this.zoomPanel);
    }

    initTabs() {
      const initialTab =
        this.root.querySelector('[data-fajmp-tabs]')?.dataset.initialTab ||
        this.tabs.find((tab) => tab.classList.contains('is-active'))?.dataset.fajmpTab ||
        this.tabs[0]?.dataset.fajmpTab;

      if (initialTab) {
        this.activateTab(initialTab, { focus: false });
      }
    }

    activateTab(tabName, { focus = false, scroll = false } = {}) {
      const activeButton = this.tabs.find(
        (button) => button.dataset.fajmpTab === tabName
      );
      const activePanel = this.tabPanels.find(
        (panel) => panel.dataset.fajmpTabPanel === tabName
      );

      if (!activeButton || !activePanel) return;

      this.tabs.forEach((button) => {
        const active = button === activeButton;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
      });

      this.tabPanels.forEach((panel) => {
        panel.hidden = panel !== activePanel;
      });

      if (focus) activeButton.focus();

      if (tabName === 'reviews') {
        window.requestAnimationFrame(() => {
          if (typeof window.jdgm?.initWidgets === 'function') {
            window.jdgm.initWidgets();
          }
          window.dispatchEvent(new Event('resize'));
        });
      }

      if (scroll) {
        this.root.querySelector('[data-fajmp-tabs]')?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'start'
        });
      }
    }

    handleKeydown(event) {
      const currentTab = event.target.closest('[data-fajmp-tab]');
      if (!currentTab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        return;
      }

      event.preventDefault();
      const currentIndex = this.tabs.indexOf(currentTab);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabs.length - 1;
      }

      const nextTab = this.tabs[nextIndex];
      if (nextTab) {
        this.activateTab(nextTab.dataset.fajmpTab, { focus: true });
      }
    }

    initGallery() {
      if (!this.mainGallery || !this.cells.length) return;

      if (typeof window.Flickity !== 'undefined') {
        this.flickity = new window.Flickity(this.mainGallery, {
          cellAlign: 'left',
          contain: true,
          draggable: true,
          adaptiveHeight: true,
          pageDots: false,
          prevNextButtons: false,
          wrapAround: false,
          imagesLoaded: true,
          initialIndex: this.activeMediaIndex,
          accessibility: true
        });

        this.flickity.on('change', (index) => {
          this.setActiveMedia(index, { selectSlider: false });
        });
      } else {
        this.mainGallery.scrollTo({ left: this.mainGallery.clientWidth * this.activeMediaIndex });
      }

      this.setActiveMedia(this.activeMediaIndex, { selectSlider: false });
    }

    handleClick(event) {
      const tabButton = event.target.closest('[data-fajmp-tab]');
      if (tabButton) {
        event.preventDefault();
        this.activateTab(tabButton.dataset.fajmpTab, { focus: false });
        return;
      }

      const openReviews = event.target.closest('[data-fajmp-open-reviews]');
      if (openReviews) {
        event.preventDefault();
        this.activateTab('reviews', { focus: false, scroll: true });
        return;
      }

      const prev = event.target.closest('[data-fajmp-prev]');
      if (prev) { event.preventDefault(); this.selectMedia(this.activeMediaIndex - 1); return; }

      const next = event.target.closest('[data-fajmp-next]');
      if (next) { event.preventDefault(); this.selectMedia(this.activeMediaIndex + 1); return; }

      const thumb = event.target.closest('[data-fajmp-thumb]');
      if (thumb) { event.preventDefault(); this.selectMedia(Number.parseInt(thumb.dataset.mediaIndex, 10)); return; }

      const imageButton = event.target.closest('[data-fajmp-image-button]');
      if (imageButton) {
        if (event.pointerType === 'mouse' && window.matchMedia('(min-width: 1100px)').matches) return;
        event.preventDefault(); this.openLightbox(); return;
      }

      const lightboxClose = event.target.closest('[data-fajmp-lightbox-close]');
      if (lightboxClose) { this.closeLightbox(); return; }
      if (event.target.closest('[data-fajmp-lightbox-prev]')) { this.changeLightbox(-1); return; }
      if (event.target.closest('[data-fajmp-lightbox-next]')) { this.changeLightbox(1); return; }

      if (event.target.closest('[data-fajmp-quantity-minus]')) { this.changeQuantity(-1); return; }
      if (event.target.closest('[data-fajmp-quantity-plus]')) { this.changeQuantity(1); return; }

      if (event.target.closest('[data-fajmp-share]')) { this.shareProduct(); return; }

      if (event.target.closest('[data-fajmp-mobile-add]')) {
        this.root.querySelector('[data-fajmp-add]')?.click();
      }
    }

    handleChange(event) {
      if (!event.target.matches('[data-fajmp-option]')) return;
      const selectedOptions = Array.from(this.root.querySelectorAll('[data-fajmp-option]'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((select) => select.value);

      const variant = this.productData.variants.find((candidate) =>
        candidate.options.every((option, index) => option === selectedOptions[index])
      );

      if (variant) this.updateVariant(variant, { updateUrl: true, selectMedia: true });
    }

    async handleSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.isQuoteOnly(this.currentVariant)) {
        this.root.querySelector('[data-fajmp-quote]')?.click();
        return;
      }

      if (this.isAdding || !this.form) return;

      const button = this.root.querySelector('[data-fajmp-add]');
      const label = this.root.querySelector('[data-fajmp-add-label]');
      this.isAdding = true;
      this.addController = new AbortController();
      if (button) button.disabled = true;
      if (label) label.textContent = this.root.dataset.addingLabel || 'Adding…';

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.form),
          signal: this.addController.signal
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || 'Unable to add this product.');

        if (label) label.textContent = this.root.dataset.addedLabel || 'Added';
        this.setMessage('Added to cart.');
        window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[FAJ product add]', error);
        this.setMessage(error.message, true);
      } finally {
        window.setTimeout(() => {
          this.isAdding = false;
          this.addController = null;
          if (button) button.disabled = false;
          if (label) label.textContent = button?.dataset.defaultLabel || this.root.dataset.addLabel || 'Add to Cart';
        }, 1100);
      }
    }

    updateVariant(variant, { updateUrl = true, selectMedia = true } = {}) {
      if (!variant) return;
      this.currentVariant = variant;
      const hasPrice = this.hasPositivePrice(variant);
      const canAddToCart = this.canAddToCart(variant);
      const quoteOnly = !canAddToCart;
      const hiddenId = this.root.querySelector('[data-fajmp-variant-id]');
      if (hiddenId) hiddenId.value = variant.id;

      this.setText('[data-fajmp-price]', this.formatMoney(variant.price));
      this.setText('[data-fajmp-mobile-price]', this.formatMoney(variant.price));
      this.setText('[data-fajmp-sku]', variant.sku || '');
      this.toggle('[data-fajmp-sku-wrap]', !variant.sku);

      const compareVisible = variant.compareAtPrice > variant.price && variant.price > 0;
      this.setText('[data-fajmp-compare]', this.formatMoney(variant.compareAtPrice));
      this.toggle('[data-fajmp-compare]', !compareVisible);

      this.toggle('[data-fajmp-price-block]', !hasPrice);
      this.toggle('[data-fajmp-quote-price]', hasPrice);
      this.toggle('[data-fajmp-bnpl]', !hasPrice);
      this.toggle('[data-fajmp-quantity-wrap]', !canAddToCart);
      this.toggle('[data-fajmp-add]', !canAddToCart);
      this.toggle('[data-fajmp-mobile-price]', !hasPrice);
      this.toggle('[data-fajmp-mobile-quote]', hasPrice);
      this.toggle('[data-fajmp-mobile-add]', !canAddToCart);
      this.toggle('[data-fajmp-mobile-quote-button]', !quoteOnly);

      const quoteVisible = quoteOnly || this.root.dataset.showQuoteAlways === 'true';
      this.toggle('[data-fajmp-quote]', !quoteVisible);

      const addButton = this.root.querySelector('[data-fajmp-add]');
      if (addButton) {
        addButton.hidden = !canAddToCart;
        addButton.disabled = !canAddToCart;
      }

      const quantityWrap = this.root.querySelector('[data-fajmp-quantity-wrap]');
      if (quantityWrap) {
        quantityWrap.hidden = !canAddToCart;
      }

      this.updateStock(variant, quoteOnly);
      this.updateQuoteLinks(variant);

      if (selectMedia && variant.featuredMediaId) {
        const index = this.cells.findIndex((cell) => String(cell.dataset.mediaId) === String(variant.featuredMediaId));
        if (index >= 0) this.selectMedia(index);
      }

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.root.dispatchEvent(new CustomEvent('faj:variant:change', {
        bubbles: true,
        detail: { variant, quoteOnly, hasPrice, canAddToCart }
      }));
      document.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant, quoteOnly, hasPrice, canAddToCart }
      }));

    }

    hasPositivePrice(variant) {
      if (!variant) return false;

      if (typeof variant.hasPrice === 'boolean') {
        return variant.hasPrice;
      }

      return Number(variant.price) > 0;
    }

    canAddToCart(variant) {
      if (!variant) return false;

      if (typeof variant.canAddToCart === 'boolean') {
        return variant.canAddToCart;
      }

      const tracked = Boolean(variant.inventoryManagement);
      const inventoryAllowsCart = tracked
        ? Number(variant.inventoryQuantity) > 0
        : Boolean(variant.available);

      return this.hasPositivePrice(variant) && inventoryAllowsCart;
    }

    isQuoteOnly(variant) {
      return !this.canAddToCart(variant);
    }

    updateStock(variant, quoteOnly) {
      const stock = this.root.querySelector('[data-fajmp-stock]');
      const text = this.root.querySelector('[data-fajmp-stock-text]');
      if (!stock || !text) return;
      stock.classList.toggle('fajmp__stock--quote', quoteOnly);
      stock.classList.toggle('fajmp__stock--available', !quoteOnly);
      if (quoteOnly) text.textContent = this.root.dataset.quoteRequiredLabel || 'Quote required';
      else if (variant.inventoryManagement && Number(variant.inventoryQuantity) > 0) text.textContent = this.root.dataset.inStockLabel || 'In stock in UAE';
      else text.textContent = this.root.dataset.availableOrderLabel || 'Available on order';
    }

    updateQuoteLinks(variant) {
      const template = this.root.dataset.quoteTemplate || 'Hello, I would like a quote for [product]: [url]';
      const message = template
        .replaceAll('[product]', this.productData.productTitle || '')
        .replaceAll('[variant]', variant?.title || '')
        .replaceAll('[sku]', variant?.sku || '')
        .replaceAll('[url]', window.location.href);
      const href = `https://wa.me/${this.root.dataset.quotePhone || ''}?text=${encodeURIComponent(message)}`;
      this.root.querySelectorAll('[data-fajmp-quote], [data-fajmp-mobile-quote-button]').forEach((link) => { link.href = href; });
    }

    selectMedia(rawIndex) {
      if (!this.cells.length) return;
      const index = (rawIndex + this.cells.length) % this.cells.length;
      if (this.flickity) this.flickity.select(index);
      else this.mainGallery?.scrollTo({ left: this.mainGallery.clientWidth * index, behavior: 'smooth' });
      this.setActiveMedia(index, { selectSlider: false });
    }

    setActiveMedia(index) {
      this.activeMediaIndex = index;
      this.thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      });
      this.hideZoom();
    }

    getContainedImageBounds(image) {
      const rect = image.getBoundingClientRect();
      const naturalWidth = image.naturalWidth || 1;
      const naturalHeight = image.naturalHeight || 1;
      const naturalRatio = naturalWidth / naturalHeight;
      const boxRatio = rect.width / rect.height;
      const objectFit = window.getComputedStyle(image).objectFit || 'contain';

      let width = rect.width;
      let height = rect.height;
      let left = rect.left;
      let top = rect.top;

      if (objectFit === 'contain' || objectFit === 'scale-down') {
        if (naturalRatio > boxRatio) {
          width = rect.width;
          height = width / naturalRatio;
          top = rect.top + (rect.height - height) / 2;
        } else {
          height = rect.height;
          width = height * naturalRatio;
          left = rect.left + (rect.width - width) / 2;
        }
      }

      return {
        left,
        top,
        right: left + width,
        bottom: top + height,
        width,
        height,
        naturalWidth,
        naturalHeight
      };
    }

    positionZoomPanel(sourceRect) {
      const panel = this.zoomPanel;
      if (!panel) return null;

      const viewportPadding = 16;
      const panelGap = 18;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const panelWidth = Math.min(
        520,
        Math.max(350, viewportWidth * 0.39),
        viewportWidth - viewportPadding * 2
      );

      const panelHeight = Math.min(
        Math.max(380, Math.min(sourceRect.height, 560)),
        viewportHeight - viewportPadding * 2
      );

      let left = sourceRect.right + panelGap;

      if (left + panelWidth > viewportWidth - viewportPadding) {
        left = sourceRect.left - panelGap - panelWidth;
      }

      left = Math.max(
        viewportPadding,
        Math.min(left, viewportWidth - panelWidth - viewportPadding)
      );

      const top = Math.max(
        viewportPadding,
        Math.min(
          sourceRect.top,
          viewportHeight - panelHeight - viewportPadding
        )
      );

      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
      panel.style.width = `${panelWidth}px`;
      panel.style.height = `${panelHeight}px`;

      return { width: panelWidth, height: panelHeight };
    }

    updateZoomImage({
      source,
      alt,
      x,
      y,
      bounds,
      panelSize,
      level
    }) {
      const zoomImage = this.zoomImage;
      if (!zoomImage) return;

      if (zoomImage.src !== source) {
        zoomImage.src = source;
      }

      zoomImage.alt = alt || '';

      const naturalWidth = bounds.naturalWidth || 1;
      const naturalHeight = bounds.naturalHeight || 1;

      const coverScale = Math.max(
        panelSize.width / naturalWidth,
        panelSize.height / naturalHeight
      );
      const scale = coverScale * Math.max(1.5, level);
      const scaledWidth = naturalWidth * scale;
      const scaledHeight = naturalHeight * scale;

      let left = panelSize.width / 2 - x * scaledWidth;
      let top = panelSize.height / 2 - y * scaledHeight;

      const minimumLeft = panelSize.width - scaledWidth;
      const minimumTop = panelSize.height - scaledHeight;

      left = Math.min(0, Math.max(minimumLeft, left));
      top = Math.min(0, Math.max(minimumTop, top));

      zoomImage.style.width = `${scaledWidth}px`;
      zoomImage.style.height = `${scaledHeight}px`;
      zoomImage.style.left = `${left}px`;
      zoomImage.style.top = `${top}px`;

      return { scaledWidth, scaledHeight };
    }

    handlePointerMove(event) {
      if (
        this.root.dataset.zoomEnabled !== 'true' ||
        !window.matchMedia('(min-width: 1100px)').matches
      ) {
        return;
      }

      const cell = this.cells[this.activeMediaIndex];
      const image = cell?.querySelector('[data-fajmp-image-button] img');
      const button = cell?.querySelector('[data-fajmp-image-button]');
      const panel = this.zoomPanel;
      const lens = this.root.querySelector('[data-fajmp-zoom-lens]');

      if (!image || !button || !panel || !lens || !image.complete) {
        this.hideZoom();
        return;
      }

      const buttonRect = button.getBoundingClientRect();
      const imageBounds = this.getContainedImageBounds(image);

      const insideImage =
        event.clientX >= imageBounds.left &&
        event.clientX <= imageBounds.right &&
        event.clientY >= imageBounds.top &&
        event.clientY <= imageBounds.bottom;

      if (!insideImage) {
        this.hideZoom();
        return;
      }

      const x = Math.max(
        0,
        Math.min(1, (event.clientX - imageBounds.left) / imageBounds.width)
      );
      const y = Math.max(
        0,
        Math.min(1, (event.clientY - imageBounds.top) / imageBounds.height)
      );

      const level = Number.parseFloat(this.root.dataset.zoomLevel) || 2.5;
      const fullSrc =
        button.dataset.fullSrc ||
        image.currentSrc ||
        image.src;

      const panelSize = this.positionZoomPanel(buttonRect);
      if (!panelSize || !fullSrc) {
        this.hideZoom();
        return;
      }

      const scaled = this.updateZoomImage({
        source: fullSrc,
        alt: image.alt,
        x,
        y,
        bounds: imageBounds,
        panelSize,
        level
      });

      if (!scaled) {
        this.hideZoom();
        return;
      }

      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      lens.hidden = false;

      const lensWidth = Math.max(
        54,
        Math.min(
          imageBounds.width,
          panelSize.width * imageBounds.width / scaled.scaledWidth
        )
      );

      const lensHeight = Math.max(
        54,
        Math.min(
          imageBounds.height,
          panelSize.height * imageBounds.height / scaled.scaledHeight
        )
      );

      lens.style.width = `${lensWidth}px`;
      lens.style.height = `${lensHeight}px`;

      const imageOffsetLeft = imageBounds.left - buttonRect.left;
      const imageOffsetTop = imageBounds.top - buttonRect.top;

      lens.style.left = `${
        imageOffsetLeft +
        Math.max(
          0,
          Math.min(
            imageBounds.width - lensWidth,
            event.clientX - imageBounds.left - lensWidth / 2
          )
        )
      }px`;

      lens.style.top = `${
        imageOffsetTop +
        Math.max(
          0,
          Math.min(
            imageBounds.height - lensHeight,
            event.clientY - imageBounds.top - lensHeight / 2
          )
        )
      }px`;
    }

    hideZoom() {
      const panel = this.zoomPanel;
      const lens = this.root.querySelector('[data-fajmp-zoom-lens]');

      if (panel) {
        panel.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
      }

      if (this.zoomImage) {
        this.zoomImage.style.left = '0px';
        this.zoomImage.style.top = '0px';
      }

      if (lens) lens.hidden = true;
    }

    openLightbox() {
      const dialog = this.root.querySelector('[data-fajmp-lightbox]');
      const image = this.root.querySelector('[data-fajmp-lightbox-image]');
      const button = this.cells[this.activeMediaIndex]?.querySelector('[data-fajmp-image-button]');
      if (!dialog || !image || !button) return;
      image.src = button.dataset.fullSrc || '';
      image.alt = button.querySelector('img')?.alt || this.productData.productTitle || '';
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }

    closeLightbox() {
      const dialog = this.root.querySelector('[data-fajmp-lightbox]');
      if (dialog?.open && typeof dialog.close === 'function') dialog.close();
      else dialog?.removeAttribute('open');
    }

    changeLightbox(direction) {
      const imageIndexes = this.cells.map((cell, index) => cell.dataset.mediaType === 'image' ? index : -1).filter((index) => index >= 0);
      if (!imageIndexes.length) return;
      const currentPosition = Math.max(0, imageIndexes.indexOf(this.activeMediaIndex));
      const nextIndex = imageIndexes[(currentPosition + direction + imageIndexes.length) % imageIndexes.length];
      this.selectMedia(nextIndex);
      this.openLightbox();
    }

    changeQuantity(delta) {
      const input = this.root.querySelector('[data-fajmp-quantity]');
      if (!input) return;
      input.value = String(Math.max(1, (Number.parseInt(input.value, 10) || 1) + delta));
    }

    async shareProduct() {
      const shareData = { title: this.productData.productTitle, url: window.location.href };
      try {
        if (navigator.share) await navigator.share(shareData);
        else {
          await navigator.clipboard.writeText(window.location.href);
          this.setMessage('Product link copied.');
        }
      } catch (error) {
        if (error.name !== 'AbortError') console.error('[FAJ product share]', error);
      }
    }

    formatMoney(cents) {
      if (typeof window.Shopify?.formatMoney === 'function') {
        try { return window.Shopify.formatMoney(cents, JSON.parse(this.root.dataset.moneyFormat || '"AED {{amount}}"')); }
        catch (error) { return window.Shopify.formatMoney(cents); }
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AE', { style: 'currency', currency: 'AED', maximumFractionDigits: 2 }).format((Number(cents) || 0) / 100);
    }

    setText(selector, value) {
      this.root.querySelectorAll(selector).forEach((element) => { element.textContent = value; });
    }

    toggle(selector, shouldHide) {
      this.root.querySelectorAll(selector).forEach((element) => { element.hidden = Boolean(shouldHide); });
    }

    setMessage(message, isError = false) {
      const element = this.root.querySelector('[data-fajmp-form-message]');
      if (!element) return;
      element.textContent = message || '';
      element.style.color = isError ? 'var(--fajmp-accent)' : 'var(--fajmp-success)';
    }
  }

  function init(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new FAJModernProduct(root));
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  }

  window[GLOBAL_KEY] = { init, initAll };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR) ? event.target : event.target.querySelector?.(ROOT_SELECTOR);
    const instance = root ? instances.get(root) : null;
    instance?.destroy();
    if (root) instances.delete(root);
  });
})();
